import { useCallback, useEffect, useMemo, useState } from 'react';

import { findEvolvingBudgetId } from '../summary/budgetUtils';

import { STATUS_ALL, STATUS_OPTIONS } from './budgetOverviewFormat';

// Its own key: the Organisation summary's selection is a different report's
// state, and sharing it would make one page's picker rewrite the other's.
const storageKey = (organisationId) => `org-projects-summary-filters:${organisationId}`;

const readPersisted = (organisationId) => {
    try {
        const raw = window.localStorage.getItem(storageKey(organisationId));

        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writePersisted = (organisationId, value) => {
    try {
        window.localStorage.setItem(storageKey(organisationId), JSON.stringify(value));
    } catch (e) {
        // Persistence is a convenience; the page works without it.
    }
};

/**
 * Project selection ({ [eventId]: { enabled, budget } }, the summary picker's
 * shape) and the Status filter, remembered per organisation. A remembered
 * version that no longer exists falls back to the latest estimate.
 *
 * The committed / actuals toggle is deliberately not remembered: it starts off
 * on every visit.
 */
export default function useBudgetOverviewFilters(organisationId, events) {
    const [filterData, setFilterData] = useState({});
    const [status, setStatusState] = useState(STATUS_ALL);
    const [committedActuals, setCommittedActualsState] = useState(false);
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        if (hydrated || !events.length) {
            return;
        }

        const persisted = readPersisted(organisationId);
        const persistedFilters = persisted?.filterData || {};

        const next = {};
        events.forEach((event) => {
            const entry = persistedFilters[event.id];
            const budgetExists = entry && (event.budgets || []).some((budget) => budget.id === entry.budget);

            next[event.id] = {
                enabled: entry ? !!entry.enabled : true,
                budget: budgetExists ? entry.budget : findEvolvingBudgetId(event),
            };
        });

        setFilterData(next);

        if (STATUS_OPTIONS.some((option) => option.value === persisted?.status)) {
            setStatusState(persisted.status);
        }

        setHydrated(true);
    }, [events, hydrated, organisationId]);

    // Archived projects arrive after hydration when "Show archived" is ticked;
    // give each one an entry so the picker and the request agree on its shape.
    useEffect(() => {
        if (!hydrated) {
            return;
        }

        const missing = events.filter((event) => !filterData[event.id]);
        if (!missing.length) {
            return;
        }

        setFilterData((previous) => {
            const next = { ...previous };
            missing.forEach((event) => {
                next[event.id] = { enabled: false, budget: findEvolvingBudgetId(event) };
            });

            return next;
        });
    }, [events, hydrated, filterData]);

    useEffect(() => {
        if (!hydrated) {
            return;
        }

        writePersisted(organisationId, { filterData, status });
    }, [filterData, status, hydrated, organisationId]);

    const selectedEvents = useMemo(() => events.filter((event) => filterData[event.id]?.enabled), [events, filterData]);

    const removeProject = useCallback((eventId) => {
        setFilterData((previous) => ({
            ...previous,
            [eventId]: { ...previous[eventId], enabled: false },
        }));
    }, []);

    const setSelection = useCallback((next) => {
        setFilterData(next || {});
    }, []);

    return {
        hydrated,
        filterData,
        status,
        committedActuals,
        selectedEvents,
        selectedCount: selectedEvents.length,
        removeProject,
        setSelection,
        setStatus: setStatusState,
        setCommittedActuals: setCommittedActualsState,
    };
}
